/* eslint-disable react/prop-types */

/* Optimized for large lists
  Based on: https://github.com/JedWatson/react-select/issues/3128#issuecomment-521242192
  with react-window
*/

import React, { memo, useMemo } from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import {
  Text, FormErrorMessage, Clamp, FormFieldInfo
} from 'components/atoms';
import Select, { createFilter, components } from 'react-select';
import Creatable from 'react-select/creatable';
import { darken } from 'polished';
import { FixedSizeList as List } from 'react-window';
import { getLabelFromOptions } from './FieldSelect'

const height = 35;

const Option = (props) => {
  const {
    // eslint-disable-next-line no-unused-vars
    innerProps, isFocused, children, ...otherProps
  } = props;

  // eslint-disable-next-line no-unused-vars
  const { onMouseMove, onMouseOver, ...otherInnerProps } = innerProps;
  const newProps = { innerProps: { ...otherInnerProps }, ...otherProps };
  return (
    <components.Option {...newProps} className="react-select-optimized-option">
      <Clamp lines={1}>{children}</Clamp>
    </components.Option>
  );
}

const MenuList = memo(({
  options, children, maxHeight, getValue
}) => {
  const [value] = getValue();
  const initialOffset = useMemo(() => options.indexOf(value) * height, [value, options])
  return (
    <List
      height={maxHeight}
      itemCount={children.length}
      itemSize={height}
      initialScrollOffset={initialOffset}
    >
      {({ index, style }) => <div style={style}>{children[index]}</div>}
    </List>
  );
})

// this spacing is used below the error message, this makes the margin feel more natural
// when the error happears and disappears
const InputSpacingFix = styled.div`
  height: ${props => props.theme.spacing.m};
  width: 100%;
`;

const FieldSelect = ({
  title,
  options,
  field,
  creatable,
  isMulti,
  info,
  form: { errors, setFieldValue },
  ...props
}) => {
  const isError = useMemo(() => errors[field.name], [errors, field.name]);

  const SelectComponent = useMemo(() => (creatable ? Creatable : Select), [
    creatable,
  ]);

  const handleChange = option => {
    if (isMulti) {
      const values = option ? option.map(({ value }) => value) : [];
      setFieldValue(field.name, values);
      return;
    }
    if (option.value) setFieldValue(field.name, option.value);
  };

  const noOptionsMessage = useMemo(() => {
    if (creatable) return 'Digite para criar uma opção';
    return 'Sem opções';
  }, [creatable]);

  const valuesComputed = useMemo(() => {
    if ((field.value || field.value === '' || field.value === 0) && field.value.length > 0) {
      return field.value.map(value => ({
        label: getLabelFromOptions(options, value),
        value
      }))
    }
    if (
      !(field.value instanceof Array)
      && (field.value || field.value === 0 || field.value === '')
    ) {
      return {
        label: getLabelFromOptions(options, field.value),
        value: field.value
      }
    }
    return null
  }, [field.value, options])

  return (
    <FieldSelectStyles isMulti={isMulti}>
      <Top>{title && <Text modifiers={['base']}>{title}</Text>}</Top>
      <SelectComponent
        classNamePrefix="react-select"
        noOptionsMessage={() => noOptionsMessage}
        options={options}
        name={field.name}
        value={valuesComputed}
        onChange={handleChange}
        onBlur={field.onBlur}
        isMulti={isMulti}
        components={{ MenuList, Option }}
        filterOption={createFilter({ ignoreAccents: false })}
        {...props}
      />
      {isError && <FormErrorMessage>{errors[field.name]}</FormErrorMessage>}
      {info && <FormFieldInfo>{info}</FormFieldInfo>}
      <InputSpacingFix />
    </FieldSelectStyles>
  );
};

FieldSelect.propTypes = {
  title: PropTypes.string,
  creatable: PropTypes.bool,
  isMulti: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ),
  field: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.any,
    onBlur: PropTypes.func,
  }).isRequired,
  form: PropTypes.shape({
    touched: PropTypes.shape({}),
    errors: PropTypes.shape({}),
    isSubmitting: PropTypes.bool,
    isValid: PropTypes.bool,
    setFieldValue: PropTypes.func,
  }).isRequired,
  info: PropTypes.string,
};

FieldSelect.defaultProps = {
  title: null,
  options: [],
  creatable: false,
  isMulti: false,
  info: null,
};

const Top = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const FieldSelectStyles = styled.div`
  ${Text} {
    padding-bottom: ${props => props.theme.spacing.s};
  }
  .react-select {
    &-optimized-option {
      &:hover {
        background-color: ${props => darken(0.04, props.theme.colors.bgLighter)};
      }
    }
    &__indicator-separator {
      padding: 0;
      margin-right: ${props => props.theme.spacing.xs3};
      ${props => !props.isMulti && 'display:none;'};
    }
    &__control {
      cursor: pointer;
      background-color: transparent;
      color: ${props => props.theme.colors.base};
      font-size: ${props => props.theme.sizes.s};
      font-weight: ${props => props.theme.weight.light};
      width: 100%;
      border: 0;
      border-radius: 0;
      border-bottom: ${props => props.theme.values.borderSize} solid
        ${props => props.theme.colors.base};
      &:hover {
        border-color: ${props => props.theme.colors.base};
      }
      &--is-focused {
        border: 0;
        border-bottom: ${props => props.theme.values.borderSize} solid
          ${props => props.theme.colors.base};
        box-shadow: none;
      }
    }

    &__dropdown-indicator {
      padding: ${props => `${props.theme.spacing.xs2} 0`};
    }

    &__value-container {
      padding: ${props => `${props.theme.spacing.xs2} 0`};
    }

    &__menu {
      border-radius: 0;
      box-shadow: none;
      &-notice {
        font-size: ${props => props.theme.sizes.s};
        font-weight: ${props => props.theme.weight.light};
      }
    }
    &__option {
      font-size: ${props => props.theme.sizes.s};
      background-color: ${props => props.theme.colors.bg};
      color: ${props => props.theme.colors.base};
      &--is-selected {
        background-color: ${props => props.theme.colors.bgDark} !important;
        color: ${props => props.theme.colors.baseInverse} !important;
      }
      &--is-focused {
        background-color: ${props => darken(0.04, props.theme.colors.bgLighter)};
        color: ${props => props.theme.colors.base};
      }
    }

    &__indicator {
      color: ${props => props.theme.colors.base};
    }

    &__multi-value {
      background-color: ${props => props.theme.colors.greyLighS};
      border-radius: ${props => props.theme.sizes.xs};
      &__label {
        font-size: ${props => props.theme.sizes.s};
      }
    }
  }
`;

export default FieldSelect;
